import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, UtensilsCrossed, Layers, ReceiptText, BarChart3, Settings, LogOut, Store, Ticket, Users as UsersIcon, QrCode, ChefHat } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, fileUrl } from "@/lib/api";

const NAV = [
  { to: "/owner", label: "Ringkasan", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/owner/menu", label: "Menu", icon: UtensilsCrossed, testid: "nav-menu" },
  { to: "/owner/options", label: "Opsi", icon: Layers, testid: "nav-options" },
  { to: "/owner/orders", label: "Pesanan", icon: ReceiptText, testid: "nav-orders" },
  { to: "/owner/kitchen", label: "Dapur", icon: ChefHat, testid: "nav-kitchen" },
  { to: "/owner/qr", label: "QR Meja", icon: QrCode, testid: "nav-qr" },
  { to: "/owner/promos", label: "Promo", icon: Ticket, testid: "nav-promos" },
  { to: "/owner/customers", label: "Pelanggan", icon: UsersIcon, testid: "nav-customers" },
  { to: "/owner/reports", label: "Laporan", icon: BarChart3, testid: "nav-reports" },
  { to: "/owner/settings", label: "Setting", icon: Settings, testid: "nav-settings" },
];

export default function OwnerLayout({ children }) {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/owner/login");
  };

  return (
    <div className="min-h-screen flex bg-[#F9F9F6]">
      <aside className="hidden md:flex md:flex-col w-64 border-r border-[#D3CEC4] bg-white">
        <div className="p-6 border-b border-[#D3CEC4]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#1E3A2B] text-white flex items-center justify-center overflow-hidden">
              {settings?.logo_path ? (
                <img src={fileUrl(settings.logo_path)} alt="logo" className="w-full h-full object-cover" />
              ) : (
                <Store className="w-5 h-5" />
              )}
            </div>
            <div>
              <div className="font-display font-semibold text-[#1E3A2B] text-sm leading-tight" data-testid="owner-restaurant-name">{settings?.restaurant_name || "Warung Kasir"}</div>
              <div className="text-xs text-[#5C6A61]">Owner Panel</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-[#C25934] text-white"
                    : "text-[#1E3A2B] hover:bg-[#EAE7DF]"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#D3CEC4]">
          <div className="px-3 py-2 text-xs text-[#5C6A61]" data-testid="owner-email">{user?.email}</div>
          <button
            onClick={onLogout}
            data-testid="btn-logout"
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-[#D64045] hover:bg-[#EAE7DF] transition-colors"
          >
            <LogOut className="w-4 h-4" /> Keluar
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-[#D3CEC4] px-4 py-3 flex items-center justify-between">
        <div className="font-display font-semibold text-[#1E3A2B]">{settings?.restaurant_name || "Warung Kasir"}</div>
        <button data-testid="btn-logout-mobile" onClick={onLogout} className="text-[#D64045] text-sm">Keluar</button>
      </div>

      <main className="flex-1 pt-14 md:pt-0 pb-20 md:pb-0">
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto">{children}</div>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#D3CEC4] grid grid-cols-10 overflow-x-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`${n.testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-2 text-[9px] gap-0.5 ${
                  isActive ? "text-[#C25934]" : "text-[#5C6A61]"
                }`
              }
            >
              <n.icon className="w-5 h-5" />
              {n.label}
            </NavLink>
          ))}
        </nav>
      </main>
    </div>
  );
}
